// WhatsApp sekmesi — bağlıyken: hat bilgisi, otomatik mesaj özeti, beyan bilgisi, şablon listesi, bağlantıyı kes.
// Şablon listesi yönetim panelindekiyle aynı bileşen (Klinik Detay › WhatsApp kartı); Meta App Review için klinikte de gösterilir.

import React, { useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { WhatsApp as WhatsAppIcon, LinkOff as LinkOffIcon } from "@mui/icons-material";
import { ActionButton, InfoRow, formatPhoneDisplay } from "../../../components/atoms";
import { SectionCard } from "../../../components/molecules";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import WhatsAppTemplateList from "../../Clinics/ClinicDetail/WhatsAppTemplateList";
import { COLORS, TYPO, CARD_PAD, dashboardSectionSx } from "../../../styles/uiConstants";
import {
  formatDateTime,
  phoneMismatch,
  historyImportText,
  qualityLabel,
  qualityTone,
  templateSummary,
  tierLabel,
} from "../../../constants/whatsapp";

const cardSx = { ...dashboardSectionSx, display: "flex", flexDirection: "column" };
const valueSx = { fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4, fontVariantNumeric: "tabular-nums" };

/**
 * @param {object} account - AccountView
 * @param {() => Promise<boolean>} onDisconnect
 * @param {boolean} disconnecting
 * @param {() => void} onSync - şablonları Meta'dan yeniler, eksikleri oluşturur
 * @param {boolean} syncing
 * @param {object} palette
 */
export default function ConnectedPanel({ account, clinicPhone, onDisconnect, disconnecting, onSync, syncing, palette }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const summary = templateSummary(account?.templates);
  const decl = account?.declaration;
  const coex = account?.coexistence || null;

  const handleDisconnect = async () => {
    const ok = await onDisconnect();
    if (ok) setConfirmOpen(false);
  };

  return (
    <Box>
      <SectionCard
        title="WhatsApp hattı"
        subtitle="Meta'daki numaranız ve gönderim durumu"
        icon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
        sx={cardSx}
        action={<StatusPill label="Bağlı" tone="success" />}
      >
        <InfoRow label="Numara">
          <Typography sx={valueSx}>
            {[account?.displayPhoneNumber, account?.verifiedName].filter(Boolean).join(" · ") || "—"}
          </Typography>
          {phoneMismatch(account?.displayPhoneNumber, clinicPhone) ? (
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.errorDark, mt: 0.25 }}>
              Klinik telefonundan farklı ({formatPhoneDisplay(clinicPhone)}). Bu numarayla yeniden bağlanılmalı.
            </Typography>
          ) : null}
        </InfoRow>
        <InfoRow label="Kalite">
          <Box sx={{ pt: 0.25 }}>
            <StatusPill label={qualityLabel(account?.qualityRating)} tone={qualityTone(account?.qualityRating)} />
          </Box>
        </InfoRow>
        <InfoRow label="Mesaj limiti">
          <Typography sx={valueSx}>{tierLabel(account?.messagingLimitTier)}</Typography>
        </InfoRow>
        <InfoRow label="Bağlandı">
          <Typography sx={valueSx}>{formatDateTime(account?.connectedAt) || "—"}</Typography>
        </InfoRow>
        <InfoRow label="Otomatik mesajlar">
          <Box sx={{ pt: 0.25, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
            <StatusPill label={summary.label} tone={summary.tone} />
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, lineHeight: 1.5 }}>{summary.text}</Typography>
          </Box>
        </InfoRow>
        <InfoRow label="Kullanım">
          <Box sx={{ pt: 0.4 }}>
            <Typography sx={{ ...valueSx, pt: 0 }}>
              {coex ? "Telefondaki WhatsApp Business + panel" : "Yalnız panel"}
            </Typography>
            {historyImportText(coex) ? (
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, lineHeight: 1.5, mt: 0.25 }}>
                {historyImportText(coex)}
              </Typography>
            ) : null}
          </Box>
        </InfoRow>
        <InfoRow label="Beyan" noBorder>
          <Typography sx={{ ...valueSx, color: COLORS.slate600 }}>
            {decl?.acceptedAt
              ? [
                  decl.version ? `Sürüm ${decl.version}` : null,
                  decl.acceptedByName,
                  formatDateTime(decl.acceptedAt),
                ]
                  .filter(Boolean)
                  .join(" · ")
              : "—"}
          </Typography>
        </InfoRow>
        <WhatsAppTemplateList
          templates={account?.templates}
          canSync={Boolean(onSync)}
          onSync={onSync}
          syncing={syncing}
          palette={palette}
        />
        {coex?.syncError ? (
          <Box sx={{ px: CARD_PAD, pb: 1.5 }}>
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: TYPO.label }}>
              Telefondaki uygulamayla eşitleme başlatılamadı. Meta bu adımı bağlantıdan sonraki 24 saat içinde ister;
              olmazsa bağlantıyı kesip yeniden bağlayın. ({coex.syncError})
            </Alert>
          </Box>
        ) : null}
        {coex ? (
          <Box sx={{ px: CARD_PAD, pb: 1.5 }}>
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, lineHeight: 1.5 }}>
              WhatsApp Business uygulamasını en az 14 günde bir telefonda açın; açılmazsa bağlantı kopar.
            </Typography>
          </Box>
        ) : null}
        <Box sx={{ px: CARD_PAD, py: 1.5, borderTop: `1px solid ${COLORS.slate100}` }}>
          <ActionButton
            variant="delete"
            tone="soft"
            label="Bağlantıyı kes"
            icon={<LinkOffIcon sx={{ fontSize: 15 }} />}
            onClick={() => setConfirmOpen(true)}
          />
        </Box>
      </SectionCard>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDisconnect}
        loading={disconnecting}
        confirmVariant="error"
        title="Bağlantıyı kes"
        confirmText="Bağlantıyı kes"
        cancelText="Vazgeç"
        message="Bağlantıyı keserseniz hastalarınıza WhatsApp'tan randevu mesajı gitmez ve WhatsApp'tan yazılanlar panele düşmez. Numara kliniğinizin Meta hesabında kalır; dilediğiniz zaman yeniden bağlayabilirsiniz."
      />
    </Box>
  );
}
